import { useState, useEffect, useMemo, useCallback } from "react";
import { PalPicker } from "@/components/PalPicker";
import { PalAvatar, ElementBadges } from "@/components/PalAvatar";
import { PalDetailProvider, usePalDetail } from "@/components/PalDetailModal";
import { usePalCompare } from "@/components/PalCompareModal";
import {
  loadMatrix, isReady, breed, findParents, pathFinder, PALS_BY_INDEX,
  loadOwned, saveOwned,
} from "@/lib/breeding";
import { api } from "@/lib/api";
import { Dna, ArrowRight, Loader2, HeartPulse, Sparkles, Search, Package, Check, Plus, Filter, Egg, Venus, Mars, Route, Scale, Star, ChevronDown, ChevronRight, Beaker } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "calc", label: "Calculateur", icon: Dna },
  { key: "finder", label: "Trouver les parents", icon: Search },
  { key: "combos", label: "Reproductions possibles", icon: Beaker },
  { key: "plan", label: "Chemin de reproduction", icon: Route },
  { key: "collection", label: "Ma collection", icon: Package },
];

const FAV_KEY = "palhub_fav_combos";
export const comboKey = (a, b, child) => `${Math.min(a, b)}-${Math.max(a, b)}-${child}`;
export function rarityMeta(r) {
  if (r >= 10) return { label: "Légendaire", c: "#FACC15" };
  if (r >= 8) return { label: "Épique", c: "#D946EF" };
  if (r >= 5) return { label: "Rare", c: "#3B82F6" };
  return { label: "Commun", c: "#94A3B8" };
}

function PalResultCard({ idx, label, big = false }) {
  const openPal = usePalDetail();
  const p = PALS_BY_INDEX[idx];
  if (!p) return null;
  return (
    <button type="button" onClick={() => openPal(idx)} className="flex flex-col items-center gap-2 group" data-testid="breed-result-child" title="Voir la fiche">
      <PalAvatar pal={p} size={big ? 84 : 56} showElement className="transition-transform group-hover:scale-105" />
      <div className="text-center">
        {label && <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/70">{label}</div>}
        <div className={`font-head font-bold group-hover:text-primary transition-colors ${big ? "text-xl" : "text-sm"}`}>{p.fr}</div>
        <div className="mt-1"><ElementBadges elements={p.elements} /></div>
      </div>
    </button>
  );
}

function GenderTag({ g }) {
  if (g === "MALE") return <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: "#60A5FA" }}><Mars className="w-3 h-3" />♂</span>;
  if (g === "FEMALE") return <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: "#F472B6" }}><Venus className="w-3 h-3" />♀</span>;
  return null;
}

function PalChip({ idx, size = 26, onClick, className = "" }) {
  const p = PALS_BY_INDEX[idx];
  if (!p) return null;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick?.(idx); }} title="Voir la fiche" className={`flex items-center gap-1.5 min-w-0 rounded-md px-1 py-0.5 hover:bg-[hsl(var(--surface-hover))] transition-colors ${className}`}>
      <PalAvatar pal={p} size={size} showElement />
      <span className="text-sm font-medium truncate">{p.fr}</span>
    </button>
  );
}

function ComboRow({ a, b, child, g, favKey, faved, toggleFav, openPal, onViewPath, showChild }) {
  return (
    <div data-testid={`combo-pair-${favKey}`} className="flex items-center gap-1.5 flex-wrap rounded-lg border border-border bg-black/20 px-2 py-1.5">
      <button type="button" data-testid={`fav-toggle-${favKey}`} onClick={() => toggleFav(favKey)} title={faved ? "Retirer des favoris" : "Ajouter aux favoris"} className="shrink-0 p-1 rounded-md hover:bg-[hsl(var(--surface-hover))] transition-colors">
        <Star className="w-4 h-4 transition-colors" style={{ color: faved ? "#FACC15" : "#475569", fill: faved ? "#FACC15" : "none" }} />
      </button>
      <PalChip idx={a} onClick={openPal} />
      {g && <GenderTag g={g.g1} />}
      <Plus className="w-3 h-3 text-muted-foreground shrink-0" />
      <PalChip idx={b} onClick={openPal} />
      {g && <GenderTag g={g.g2} />}
      {showChild && (
        <>
          <ArrowRight className="w-4 h-4 text-primary shrink-0" />
          <PalChip idx={child} onClick={openPal} className="font-bold" />
        </>
      )}
      <button type="button" onClick={() => onViewPath(child)} className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold hover:bg-primary/20 transition-colors">
        <Route className="w-3 h-3" /> Chemin
      </button>
    </div>
  );
}

function CombosPanel({ owned, favs, toggleFav, onViewPath }) {
  const openPal = usePalDetail();
  const [q, setQ] = useState("");
  const [rareFirst, setRareFirst] = useState(true);
  const [favOnly, setFavOnly] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const obtainable = useMemo(() => {
    const map = new Map(); // child -> [{a,b,g}]
    const arr = [...owned];
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const r = breed(arr[i], arr[j]);
        if (!r) continue;
        const kids = r.gendered ? r.gendered.map((x) => ({ child: x.child, g: { g1: x.g1, g2: x.g2 } })) : [{ child: r.child }];
        for (const k of kids) {
          if (k.child == null || k.child === arr[i] || k.child === arr[j]) continue;
          if (!map.has(k.child)) map.set(k.child, []);
          map.get(k.child).push({ a: arr[i], b: arr[j], g: k.g });
        }
      }
    }
    return map;
  }, [owned]);

  const children = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = [...obtainable.entries()].map(([child, pairs]) => ({ child, pairs }));
    if (s) {
      list = list.filter(({ child, pairs }) => {
        const cp = PALS_BY_INDEX[child];
        if (cp && (cp.fr.toLowerCase().includes(s) || cp.name.toLowerCase().includes(s))) return true;
        return pairs.some((pr) => {
          const pa = PALS_BY_INDEX[pr.a], pb = PALS_BY_INDEX[pr.b];
          return (pa && pa.fr.toLowerCase().includes(s)) || (pb && pb.fr.toLowerCase().includes(s));
        });
      });
    }
    list.sort((x, y) => {
      const rx = PALS_BY_INDEX[x.child]?.rarity ?? 0;
      const ry = PALS_BY_INDEX[y.child]?.rarity ?? 0;
      if (rx !== ry) return rareFirst ? ry - rx : rx - ry;
      return (PALS_BY_INDEX[x.child]?.fr || "").localeCompare(PALS_BY_INDEX[y.child]?.fr || "");
    });
    return list;
  }, [obtainable, q, rareFirst]);

  const favList = useMemo(() => {
    return [...favs].map((k) => {
      const [a, b, child] = k.split("-").map(Number);
      return { key: k, a, b, child };
    }).sort((x, y) => (PALS_BY_INDEX[y.child]?.rarity ?? 0) - (PALS_BY_INDEX[x.child]?.rarity ?? 0));
  }, [favs]);

  const toggleExpand = (c) => setExpanded((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });

  if (owned.size < 2) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-8 text-center text-sm text-amber-300" data-testid="combos-need-owned">
        <Beaker className="w-8 h-8 mx-auto mb-2 opacity-60" />
        Ajoute au moins <b>deux</b> Pals dans « <span className="font-semibold">Ma collection</span> » pour voir toutes les reproductions possibles.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="breed-combos">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="combos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un Pal ou un parent…" className="w-full rounded-lg border border-border bg-[hsl(var(--surface))] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50" />
        </div>
        <button data-testid="combos-sort" onClick={() => setRareFirst((v) => !v)} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-[hsl(var(--surface))] px-3 py-2.5 text-xs font-semibold hover:border-primary/50 transition-colors">
          <Sparkles className="w-3.5 h-3.5 text-primary" /> {rareFirst ? "Rares d'abord" : "Communs d'abord"}
        </button>
        <button data-testid="combos-fav-toggle" onClick={() => setFavOnly((v) => !v)} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors ${favOnly ? "border-[#FACC15] text-[#FACC15] bg-[#FACC1514]" : "border-border hover:border-primary/50"}`}>
          <Star className="w-3.5 h-3.5" style={{ fill: favOnly ? "#FACC15" : "none" }} /> Favoris ({favs.size})
        </button>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="combos-count">
        {favOnly ? `${favList.length} combinaison(s) en favori` : `${children.length} Pal(s) reproductible(s) directement avec ta collection (${owned.size} possédés)`}
      </div>

      {favOnly ? (
        favList.length === 0 ? (
          <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center text-sm text-muted-foreground" data-testid="combos-fav-empty">
            <Star className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aucun favori. Ouvre un Pal ci-dessous et clique sur l'étoile d'une combinaison pour l'enregistrer.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="combos-fav-list">
            {favList.map((f) => (
              <ComboRow key={f.key} a={f.a} b={f.b} child={f.child} favKey={f.key} faved={true} toggleFav={toggleFav} openPal={openPal} onViewPath={onViewPath} showChild />
            ))}
          </div>
        )
      ) : children.length === 0 ? (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center text-sm text-muted-foreground" data-testid="combos-empty">
          <Beaker className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucune reproduction ne correspond. Essaie un autre nom ou ajoute des Pals à ta collection.
        </div>
      ) : (
        <div className="space-y-2" data-testid="combos-list">
          {children.map(({ child, pairs }) => {
            const cp = PALS_BY_INDEX[child];
            const rm = rarityMeta(cp?.rarity ?? 0);
            const isOpen = expanded.has(child);
            const sortedPairs = [...pairs].sort((x, y) => ((PALS_BY_INDEX[x.a]?.rarity ?? 0) + (PALS_BY_INDEX[x.b]?.rarity ?? 0)) - ((PALS_BY_INDEX[y.a]?.rarity ?? 0) + (PALS_BY_INDEX[y.b]?.rarity ?? 0)));
            const favCount = pairs.filter((pr) => favs.has(comboKey(pr.a, pr.b, child))).length;
            return (
              <div key={child} data-testid={`combo-child-${child}`} className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden">
                <button type="button" data-testid={`combo-expand-${child}`} onClick={() => toggleExpand(child)} className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[hsl(var(--surface-hover))] transition-colors text-left">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <span onClick={(e) => { e.stopPropagation(); openPal(child); }} className="shrink-0"><PalAvatar pal={cp} size={34} showElement /></span>
                  <span className="font-head font-bold text-sm truncate flex-1">{cp?.fr}</span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0" style={{ color: rm.c, background: `${rm.c}1a`, border: `1px solid ${rm.c}55` }}>{rm.label}</span>
                  {favCount > 0 && <Star className="w-3.5 h-3.5 shrink-0" style={{ color: "#FACC15", fill: "#FACC15" }} />}
                  <span className="text-xs text-muted-foreground shrink-0">{pairs.length} combi{pairs.length > 1 ? "s" : ""}</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-border" data-testid={`combo-pairs-${child}`}>
                    {sortedPairs.map((pr) => {
                      const key = comboKey(pr.a, pr.b, child);
                      return <ComboRow key={key} a={pr.a} b={pr.b} child={child} g={pr.g} favKey={key} faved={favs.has(key)} toggleFav={toggleFav} openPal={openPal} onViewPath={onViewPath} showChild={false} />;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function PairRow({ pair, testid }) {
  const openPal = usePalDetail();
  const [a, b, meta] = pair;
  const pa = PALS_BY_INDEX[a];
  const pb = PALS_BY_INDEX[b];
  if (!pa || !pb) return null;
  return (
    <div data-testid={testid} className="flex items-center gap-2 px-3 py-2.5 border-b border-border last:border-0 hover:bg-[hsl(var(--surface-hover))] transition-colors">
      <button type="button" onClick={() => openPal(a)} className="flex items-center gap-2 flex-1 min-w-0 hover:text-primary transition-colors" title="Voir la fiche">
        <PalAvatar pal={pa} size={30} showElement />
        <span className="text-sm font-medium truncate">{pa.fr}</span>
        {meta && <GenderTag g={meta.g1} />}
      </button>
      <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <button type="button" onClick={() => openPal(b)} className="flex items-center gap-2 flex-1 min-w-0 justify-end hover:text-primary transition-colors" title="Voir la fiche">
        {meta && <GenderTag g={meta.g2} />}
        <span className="text-sm font-medium truncate text-right">{pb.fr}</span>
        <PalAvatar pal={pb} size={30} showElement />
      </button>
    </div>
  );
}

function CalcPanel({ pa, pb, setPa, setPb, result }) {
  const openCompare = usePalCompare();
  return (
    <div className="space-y-5" data-testid="breed-calc">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Parent 1</label>
          <PalPicker value={pa} onChange={setPa} testid="calc-parent-a" placeholder="Parent 1…" />
        </div>
        <div className="hidden sm:flex items-center justify-center pt-5"><HeartPulse className="w-5 h-5 text-primary/70" /></div>
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Parent 2</label>
          <PalPicker value={pb} onChange={setPb} testid="calc-parent-b" placeholder="Parent 2…" />
        </div>
      </div>
      {pa != null && pb != null && (
        <div className="flex justify-center">
          <button
            data-testid="calc-compare-parents"
            onClick={() => openCompare(pa, pb)}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 text-primary px-4 py-1.5 text-xs font-semibold hover:bg-primary/20 transition-colors"
          >
            <Scale className="w-3.5 h-3.5" /> Comparer les deux parents
          </button>
        </div>
      )}
      <div className="rounded-xl border border-border bg-[hsl(var(--surface))] p-6 min-h-[160px] flex items-center justify-center" data-testid="calc-result-box">
        {(pa == null || pb == null) && (
          <div className="text-center text-sm text-muted-foreground">
            <Egg className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Choisis deux parents pour voir l'enfant obtenu.
          </div>
        )}
        {pa != null && pb != null && result?.gendered && (
          <div className="w-full space-y-3">
            <div className="text-center text-xs text-amber-300 flex items-center justify-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Résultat selon le sexe des parents
            </div>
            <div className="grid grid-cols-2 gap-4">
              {result.gendered.map((g, i) => (
                <div key={i} className="rounded-lg border border-border p-3 flex flex-col items-center gap-1">
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1"><GenderTag g={g.g1} /> + <GenderTag g={g.g2} /></div>
                  <PalResultCard idx={g.child} />
                </div>
              ))}
            </div>
          </div>
        )}
        {pa != null && pb != null && result && !result.gendered && (
          <div className="flex items-center gap-5 sm:gap-8">
            <PalAvatar pal={PALS_BY_INDEX[pa]} size={56} showElement />
            <Plus className="w-4 h-4 text-muted-foreground" />
            <PalAvatar pal={PALS_BY_INDEX[pb]} size={56} showElement />
            <ArrowRight className="w-6 h-6 text-primary" />
            <PalResultCard idx={result.child} label="Enfant" big />
          </div>
        )}
      </div>
    </div>
  );
}

function FinderPanel({ target, setTarget, knownParent, setKnownParent, ownedOnly, setOwnedOnly, owned, parents }) {
  return (
    <div className="space-y-4" data-testid="breed-finder">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Pal recherché (enfant)</label>
          <PalPicker value={target} onChange={setTarget} testid="finder-target" placeholder="Quel Pal veux-tu obtenir ?" />
        </div>
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Parent connu (optionnel)</label>
          <PalPicker value={knownParent} onChange={setKnownParent} testid="finder-known" placeholder="Filtrer par un parent…" />
        </div>
      </div>
      <label className="flex items-center gap-2.5 text-sm cursor-pointer select-none" data-testid="finder-owned-toggle">
        <input type="checkbox" checked={ownedOnly} onChange={(e) => setOwnedOnly(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" />
        <Filter className="w-4 h-4 text-primary" />
        <span>Utiliser uniquement les Pals de <span className="text-primary font-semibold">ma collection</span> ({owned.size})</span>
      </label>
      {target == null && (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center text-sm text-muted-foreground">
          <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Choisis un Pal cible pour voir toutes les paires de parents possibles.
        </div>
      )}
      {target != null && <FinderResults target={target} parents={parents} ownedOnly={ownedOnly} />}
    </div>
  );
}

function FinderResults({ target, parents, ownedOnly }) {
  const p = PALS_BY_INDEX[target];
  return (
    <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden" data-testid="finder-results">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-black/20">
        <PalAvatar pal={p} size={40} showElement />
        <div>
          <div className="font-head font-semibold text-sm">{p.fr}</div>
          <div className="text-xs text-muted-foreground" data-testid="finder-count">
            {parents.length} combinaison{parents.length > 1 ? "s" : ""} trouvée{parents.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>
      {parents.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">
          {ownedOnly ? "Aucune paire possible avec ta collection actuelle. Ajoute plus de Pals !" : "Aucune combinaison trouvée."}
        </div>
      )}
      {parents.length > 0 && (
        <div className="max-h-[520px] overflow-y-auto scrollbar-thin">
          {parents.slice(0, 300).map((pair, i) => <PairRow key={i} pair={pair} testid={`finder-pair-${i}`} />)}
          {parents.length > 300 && (
            <div className="px-4 py-3 text-center text-xs text-muted-foreground">… {parents.length - 300} autres combinaisons (affine avec un parent connu).</div>
          )}
        </div>
      )}
    </div>
  );
}

// Gender tags for a unique gendered combo step (a + b -> child), if any.
function genderFor(a, b, child) {
  const r = breed(a, b);
  if (r?.gendered) {
    const g = r.gendered.find((x) => x.child === child);
    if (g) return { g1: g.g1, g2: g.g2 };
  }
  return null;
}

function PathStepParent({ idx, bred }) {
  const openPal = usePalDetail();
  const p = PALS_BY_INDEX[idx];
  if (!p) return null;
  return (
    <button type="button" onClick={() => openPal(idx)} title="Voir la fiche" className={`flex items-center gap-1.5 min-w-0 rounded-md px-1.5 py-1 transition-colors ${bred ? "bg-primary/10 border border-primary/40" : "hover:bg-[hsl(var(--surface-hover))]"}`}>
      <PalAvatar pal={p} size={30} showElement />
      <span className={`text-sm font-medium truncate ${bred ? "text-primary" : ""}`}>{p.fr}</span>
      {bred ? (
        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-primary/15 text-primary border border-primary/40 shrink-0">à reproduire</span>
      ) : (
        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-green-500/15 text-green-400 border border-green-500/40 shrink-0"><Check className="w-2.5 h-2.5" /> possédé</span>
      )}
    </button>
  );
}

function PathFinderPanel({ planTarget, setPlanTarget, owned, plan }) {
  const openPal = usePalDetail();
  const p = planTarget != null ? PALS_BY_INDEX[planTarget] : null;
  // A parent is "bred" (produced in an earlier step) if it's not in the owned set.
  const isBred = (idx) => !owned.has(idx);
  return (
    <div className="space-y-4" data-testid="breed-plan">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Pal cible</label>
          <PalPicker value={planTarget} onChange={setPlanTarget} testid="plan-target" placeholder="Le Pal que tu veux obtenir…" />
        </div>
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] px-3 py-2.5 flex items-center gap-2">
          <Package className="w-4 h-4 text-primary shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Le chemin part <span className="text-primary font-semibold">uniquement de tes Pals possédés</span> ({owned.size}). Ajoute-les dans l'onglet <span className="text-primary font-semibold">Ma collection</span>.
          </p>
        </div>
      </div>

      {planTarget == null && (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center text-sm text-muted-foreground">
          <Route className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Choisis un Pal cible pour voir la chaîne de reproduction la plus courte depuis tes Pals.
        </div>
      )}

      {planTarget != null && plan && plan.owned && (
        <div className="rounded-lg border border-green-500/40 bg-green-500/5 px-4 py-3 text-sm text-green-400 font-semibold flex items-center gap-2" data-testid="plan-already-owned">
          <Check className="w-4 h-4 shrink-0" /> Tu possèdes déjà {p?.fr} !
          {Array.isArray(plan.steps) && plan.steps.length > 0 && (
            <span className="text-green-300/80 font-normal">Voici quand même une chaîne pour le re-produire ci-dessous.</span>
          )}
        </div>
      )}

      {planTarget != null && plan && plan.steps === null && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 text-center text-sm text-amber-300" data-testid={owned.size < 2 ? "plan-need-owned" : "plan-impossible"}>
          {owned.size < 2
            ? <>Ajoute au moins <b>deux</b> Pals dans « Ma collection » pour calculer un chemin.</>
            : <><b>{p?.fr}</b> ne peut pas être reproduit depuis tes autres Pals possédés. Ajoute d'autres Pals à ta collection.</>}
        </div>
      )}

      {planTarget != null && plan && Array.isArray(plan.steps) && plan.steps.length > 0 && (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden" data-testid="plan-results">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-black/20">
            <button type="button" onClick={() => openPal(planTarget)} className="shrink-0" title="Voir la fiche"><PalAvatar pal={p} size={40} showElement /></button>
            <div className="flex-1">
              <div className="font-head font-semibold text-sm">{p.fr}</div>
              <div className="text-xs text-muted-foreground" data-testid="plan-steps">
                chaîne la plus courte · <span className="text-primary font-semibold">{plan.steps.length}</span> reproduction{plan.steps.length > 1 ? "s" : ""} depuis ta collection
              </div>
            </div>
          </div>
          <ol className="p-4 space-y-2" data-testid="plan-steps-list">
            {plan.steps.map((s, i) => {
              const child = PALS_BY_INDEX[s.child];
              const isTarget = s.child === planTarget;
              const g = genderFor(s.a, s.b, s.child);
              return (
                <li key={`${s.child}-${i}`} data-testid={`plan-step-${s.child}`} className={`rounded-lg border p-2.5 ${isTarget ? "border-primary/60 bg-primary/5" : "border-border bg-black/20"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <PathStepParent idx={s.a} bred={isBred(s.a)} />
                    {g && <GenderTag g={g.g1} />}
                    <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <PathStepParent idx={s.b} bred={isBred(s.b)} />
                    {g && <GenderTag g={g.g2} />}
                    <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                    <button type="button" onClick={() => openPal(s.child)} className="flex items-center gap-1.5 min-w-0 hover:text-primary transition-colors" title="Voir la fiche">
                      <PalAvatar pal={child} size={30} showElement />
                      <span className={`text-sm font-bold truncate ${isTarget ? "text-primary" : ""}`}>{child.fr}</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
          {plan.steps.length > 1 && (
            <p className="mx-4 mb-4 text-[11px] text-muted-foreground">
              Les Pals marqués <span className="text-primary font-semibold">« à reproduire »</span> sont obtenus dans une étape précédente. Suis les étapes dans l'ordre.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CollectionPanel({ owned, setOwned, persistOwned, colQ, setColQ, colResults, toggleOwned }) {
  return (
    <div className="space-y-4" data-testid="breed-collection">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">Sélectionne les Pals que tu possèdes. <span className="text-primary font-semibold">{owned.size}</span> sélectionné(s).</div>
        {owned.size > 0 && (
          <button onClick={() => { setOwned(new Set()); persistOwned(new Set()); toast.success("Collection vidée"); }} data-testid="collection-clear" className="text-xs text-muted-foreground hover:text-red-400 transition-colors">Tout désélectionner</button>
        )}
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={colQ} onChange={(e) => setColQ(e.target.value)} placeholder="Rechercher un Pal à ajouter…" data-testid="collection-search" className="w-full rounded-lg border border-border bg-[hsl(var(--surface))] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {colResults.map((p) => (
          <CollectionCard key={p.i} pal={p} has={owned.has(p.i)} onToggle={toggleOwned} />
        ))}
      </div>
    </div>
  );
}

function CollectionCard({ pal, has, onToggle }) {
  const openPal = usePalDetail();
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border p-2 transition-colors ${has ? "border-primary bg-primary/10" : "border-border bg-[hsl(var(--surface))] hover:border-primary/40"}`}>
      <button type="button" onClick={(e) => { e.stopPropagation(); openPal(pal.i); }} title="Voir la fiche" className="shrink-0">
        <PalAvatar pal={pal} size={36} showElement />
      </button>
      <button type="button" onClick={() => onToggle(pal.i)} data-testid={`collection-pal-${pal.i}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{pal.fr}</div>
          <div className="text-[10px] text-muted-foreground">Pw {pal.power}</div>
        </div>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${has ? "bg-primary text-primary-foreground" : "border border-border"}`}>
          {has && <Check className="w-3.5 h-3.5" />}
        </span>
      </button>
    </div>
  );
}

export default function Breeding() {
  const [ready, setReady] = useState(isReady());
  const [loadErr, setLoadErr] = useState(false);
  const [tab, setTab] = useState("calc");

  useEffect(() => { loadMatrix().then(() => setReady(true)).catch(() => setLoadErr(true)); }, []);

  const [pa, setPa] = useState(null);
  const [pb, setPb] = useState(null);
  const result = useMemo(() => (ready && pa != null && pb != null ? breed(pa, pb) : null), [ready, pa, pb]);

  const [target, setTarget] = useState(null);
  const [knownParent, setKnownParent] = useState(null);
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [owned, setOwned] = useState(() => loadOwned());

  useEffect(() => {
    api.get("/breeding/owned").then(({ data }) => {
      const s = new Set(data.owned || []);
      setOwned(s); saveOwned(s);
    }).catch(() => {});
  }, []);

  const persistOwned = useCallback((set) => {
    saveOwned(set);
    api.put("/breeding/owned", { owned: [...set] }).catch(() => {});
  }, []);

  const parents = useMemo(() => {
    if (!ready || target == null) return [];
    return findParents(target, { ownedOnly, ownedSet: owned, parentFilter: knownParent });
  }, [ready, target, ownedOnly, owned, knownParent]);

  const [planTarget, setPlanTarget] = useState(null);
  const plan = useMemo(() => {
    if (!ready || planTarget == null) return undefined;
    return pathFinder(planTarget, owned);
  }, [ready, planTarget, owned]);

  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); }
  });
  useEffect(() => {
    api.get("/breeding/favorites").then(({ data }) => {
      const s = new Set(data.favorites || []);
      setFavs(s);
      localStorage.setItem(FAV_KEY, JSON.stringify([...s]));
    }).catch(() => {});
  }, []);
  const toggleFav = useCallback((key) => setFavs((prev) => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    localStorage.setItem(FAV_KEY, JSON.stringify([...n]));
    api.put("/breeding/favorites", { favorites: [...n] }).catch(() => {});
    return n;
  }), []);
  const viewPath = useCallback((idx) => { setPlanTarget(idx); setTab("plan"); }, []);

  const [colQ, setColQ] = useState("");
  const toggleOwned = useCallback((i) => {
    setOwned((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      persistOwned(next);
      return next;
    });
  }, [persistOwned]);

  const colResults = useMemo(() => {
    const s = colQ.trim().toLowerCase();
    let list = PALS_BY_INDEX;
    if (s) list = list.filter((p) => p.fr.toLowerCase().includes(s) || p.name.toLowerCase().includes(s));
    return list;
  }, [colQ]);

  if (!ready) {
    if (loadErr) {
      return (
        <div className="fade-up max-w-md mx-auto text-center py-24" data-testid="breeding-load-error">
          <Dna className="w-10 h-10 mx-auto mb-3 text-red-400/70" />
          <p className="text-sm text-muted-foreground">Impossible de charger les données de breeding.</p>
          <button onClick={() => { setLoadErr(false); loadMatrix().then(() => setReady(true)).catch(() => setLoadErr(true)); }} className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Réessayer</button>
        </div>
      );
    }
    return <div className="flex items-center justify-center py-24 text-primary"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <PalDetailProvider>
    <div className="fade-up max-w-4xl space-y-6" data-testid="breeding-page">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Reproduction</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Dna className="w-7 h-7 text-primary" /> Calculateur de Breeding
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {PALS_BY_INDEX.length} Pals (toutes variantes incluses) · combinaisons uniques officielles prises en compte.
        </p>
      </div>

      <div className="flex gap-1 p-1 rounded-lg border border-border bg-[hsl(var(--surface))] w-full sm:w-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`breed-tab-${key}`} className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${tab === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {tab === "calc" && <CalcPanel pa={pa} pb={pb} setPa={setPa} setPb={setPb} result={result} />}
      {tab === "finder" && <FinderPanel target={target} setTarget={setTarget} knownParent={knownParent} setKnownParent={setKnownParent} ownedOnly={ownedOnly} setOwnedOnly={setOwnedOnly} owned={owned} parents={parents} />}
      {tab === "combos" && <CombosPanel owned={owned} favs={favs} toggleFav={toggleFav} onViewPath={viewPath} />}
      {tab === "plan" && <PathFinderPanel planTarget={planTarget} setPlanTarget={setPlanTarget} owned={owned} plan={plan} />}
      {tab === "collection" && <CollectionPanel owned={owned} setOwned={setOwned} persistOwned={persistOwned} colQ={colQ} setColQ={setColQ} colResults={colResults} toggleOwned={toggleOwned} />}
    </div>
    </PalDetailProvider>
  );
}
